import { useRouter } from "next/router";

import { ChevronRight as ChevronRightIcon } from "@/assets/icons/24/ChevronRight";
import { cn } from "@/common/utils/css.utils";
import { MegaMenuMobileActionProps } from "@/components/mega-menu/mobile/MegaMenuMobileActionDelegator";
import { MegaMenuMobileBack } from "@/components/mega-menu/mobile/MegaMenuMobileBack";
import { MegaMenuMobileClose } from "@/components/mega-menu/mobile/MegaMenuMobileClose";
import { DisplayCurrencyName } from "@/features/display-currency/components/DisplayCurrencyName";
import { DISPLAY_CURRENCIES } from "@/features/display-currency/display-currency.sync";
import {
  useDisplayCurrencyEnabled,
  usePickedDisplayCurrency,
} from "@/features/display-currency/useDisplayCurrency";
import { useSetDisplayCurrency } from "@/features/display-currency/useSetDisplayCurrency";
import { isUsLocale } from "@/i18n/config";
import { Flavor } from "@/ui/typography/Flavor";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
} from "@radix-ui/react-dialog";

/**
 * The display-currency picker in the mobile menu, built like the language one (WHO-5143).
 * Renders nothing while the `web_display_currency` gate is off, and on `/en-us`, whose address
 * sets USD (WHO-6510).
 */
export const MegaMenuMobileCurrencyAction = ({
  onClick,
  children,
  className,
  ...props
}: MegaMenuMobileActionProps) => {
  const enabled = useDisplayCurrencyEnabled();
  const picked = usePickedDisplayCurrency();
  const setDisplayCurrency = useSetDisplayCurrency();
  const { locale } = useRouter();

  if (!enabled || !picked || isUsLocale(locale)) {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          {...props}
          className={cn("flex w-full items-center text-left", className)}
          data-testid="mobile-currency-switcher"
        >
          <Flavor className="flex-1">{children}</Flavor>
          <Flavor className="mr-1 text-grey-500">{picked}</Flavor>
          <ChevronRightIcon />
        </button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="fixed inset-0 z-[1000] flex flex-col space-y-6 bg-white p-4">
          <div className="flex items-center space-x-2 border-b border-b-grey-300 pb-4">
            <MegaMenuMobileBack />
            <span className="flex-1">{children}</span>
            <MegaMenuMobileClose onClick={onClick} />
          </div>
          <ul className="flex flex-col space-y-4">
            {DISPLAY_CURRENCIES.map(currency => (
              <li
                key={currency}
                className="font-title text-sm hover:text-attention-500"
              >
                <button
                  type="button"
                  className={cn(
                    "text-left",
                    picked === currency && "font-bold text-attention-500"
                  )}
                  aria-pressed={picked === currency}
                  onClick={() => {
                    setDisplayCurrency(currency);
                    onClick?.();
                  }}
                >
                  <Flavor>
                    <DisplayCurrencyName currency={currency} />
                  </Flavor>
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
